import { Controller } from "@hotwired/stimulus"
import { pickerTexts, peopleRows, selectedRows, checkInviteEmail, attachWhyTips, newMemberColor, newChipTag } from "../lib/member_picker"

export default class extends Controller {
  static targets = [
    "companyInput", "companyHidden", "companyResults",
    "addMembersBtn", "popover", "popoverSearch", "popoverList",
    "selectedList", "selectedCount", "pickerCompany",
    "pickerInviteToggle", "pickerInviteRow", "pickerInviteInput", "pickerInviteError", "pickerInviteBtn",
    "inviteModal", "inviteModalCompany", "inviteEmailInput", "inviteEmailError", "inviteSubmitBtn",
    "memberChips", "chip",
    "hiddenEntry",
    "card",
    "currencySection", "currencySelect",
    "removeModal", "removeModalCompany", "removeModalCount",
    "flexRoleSelect", "flexRoleCustom"
  ]

  static values = {
    membersUrl: String,
    validateEmailUrl: String,
    role: String,
    perCurrency: { type: Boolean, default: false },
    initialCompanyId: String,
    initialCompanyName: String,
    initialMembers: { type: Array, default: [] }
  }

  connect() {
    this._members = []
    this._membersLoaded = false
    this._selected = new Set()
    this._names = new Map() // email → name, for the pop-up's "On this project" list
    this._onClickOutside = (e) => {
      // A row the pop-up just redrew is no longer in the page: not an outside click.
      if (!e.target.isConnected) return
      if (this.hasPopoverTarget && !this.popoverTarget.contains(e.target) &&
          (!this.hasAddMembersBtnTarget || !this.addMembersBtnTarget.contains(e.target))) {
        this._closePopover()
      }
    }
    document.addEventListener("click", this._onClickOutside)
    // The hover tips on Auto / AA / New tags in the Add Members pop-up.
    this._whyTips = attachWhyTips(this.hasPopoverTarget ? this.popoverTarget : null)

    // Pre-populate card if initial values are present
    if (this.initialCompanyIdValue && this.initialCompanyNameValue) {
      this._prePopulate()
    }
  }

  _prePopulate() {
    // Set company input and hidden
    if (this.hasCompanyInputTarget) {
      const isSelect = this.companyInputTarget.tagName === "SELECT"
      this.companyInputTarget.value = isSelect ? this.initialCompanyIdValue : this.initialCompanyNameValue
    }
    if (this.hasCompanyHiddenTarget) this.companyHiddenTarget.value = this.initialCompanyIdValue
    this._companyName = this.initialCompanyNameValue
    this._appliedCompanyId = this.initialCompanyIdValue

    // Show add members button
    if (this.hasAddMembersBtnTarget) {
      this.addMembersBtnTarget.classList.remove("hidden")
      this.addMembersBtnTarget.classList.add("inline-flex")
    }

    // Add existing member chips
    this.initialMembersValue.forEach(m => {
      const email = m.email
      const name = m.name || email
      this._selected.add(email)
      this._addChip(name, email)
    })

    // Load members list for the popover
    this._loadMembers(this.initialCompanyIdValue)
  }

  // Clear existing member chips and the selected set. Called when the active
  // company changes so chips from the previous company aren't submitted under
  // the new company (the backend rejects this via validate_company_membership,
  // and even when it doesn't, the UX is misleading).
  _clearChips() {
    this._selected.clear()
    if (this.hasMemberChipsTarget) {
      this.memberChipsTarget.innerHTML = ""
    }
  }

  _applyCompanyChange(id) {
    if (id && id !== this._appliedCompanyId) {
      this._clearChips()
    }
    this._appliedCompanyId = id || ""
  }

  disconnect() {
    document.removeEventListener("click", this._onClickOutside)
    document.removeEventListener("keydown", this._onPickerKey)
    this._whyTips?.destroy()
    if (this._onCompanyCreated) {
      document.removeEventListener("new-company:created", this._onCompanyCreated)
    }
  }

  // ── Company Selected (from autocomplete) ──────────────────────────────

  companySelected(event) {
    const { id, name } = event.detail
    this._applyCompanyChange(id)
    this._companyName = name
    this._loadMembers(id)
  }

  // ── Company Selected (from dropdown select) ─────────────────────────

  dropdownChanged(event) {
    const select = event.currentTarget
    const id = select.value
    const name = select.options[select.selectedIndex]?.text || ""
    if (this.hasCompanyHiddenTarget) this.companyHiddenTarget.value = id
    this._applyCompanyChange(id)
    this._companyName = name
    if (id) {
      this._loadMembers(id)
    } else {
      if (this.hasAddMembersBtnTarget) {
        this.addMembersBtnTarget.classList.add("hidden")
        this.addMembersBtnTarget.classList.remove("inline-flex")
      }
    }
  }

  registerCompany(event) {
    // Open the new-company modal scoped to the same parent modal (avoids picking
    // a duplicate rendered inside a different hidden modal on the page)
    const parentModal = this.element.closest('[data-modal-id-value]')
    const modal = parentModal
      ? parentModal.querySelector('[data-modal-id-value="new-company"]')
      : document.querySelector('[data-modal-id-value="new-company"]')
    if (modal) {
      modal.classList.remove("hidden")
      document.body.style.overflow = "hidden"
      // Start the Name with what was typed in the search, so it isn't typed twice.
      const typed = (event?.detail?.query ?? (this.hasCompanyInputTarget ? this.companyInputTarget.value : "")).trim()
      const nameField = modal.querySelector('[data-new-company-target="name"]')
      if (nameField && typed) nameField.value = typed
    }

    // Remove previous listener before adding a new one
    if (this._onCompanyCreated) {
      document.removeEventListener("new-company:created", this._onCompanyCreated)
    }

    // Listen for company creation (one-time)
    this._onCompanyCreated = (event) => {
      const { id, name } = event.detail
      if (this.hasCompanyInputTarget) this.companyInputTarget.value = name
      if (this.hasCompanyHiddenTarget) this.companyHiddenTarget.value = id
      this._applyCompanyChange(id)
      this._loadMembers(id)
      document.removeEventListener("new-company:created", this._onCompanyCreated)
    }
    document.addEventListener("new-company:created", this._onCompanyCreated)
  }

  async _loadMembers(companyId) {
    if (!this.membersUrlValue) return

    this._membersLoaded = false
    try {
      const url = `${this.membersUrlValue}?id=${companyId}`
      const resp = await fetch(url, { headers: { "Accept": "application/json" } })
      if (!resp.ok) return
      const data = await resp.json()
      if (!Array.isArray(data)) return
      this._members = data
      this._membersLoaded = true
      if (this._pickerOpen()) this._renderPopoverList(this.hasPopoverSearchTarget ? this.popoverSearchTarget.value : "")

      // Show the "+ Members" button
      if (this.hasAddMembersBtnTarget) {
        this.addMembersBtnTarget.classList.remove("hidden")
        this.addMembersBtnTarget.classList.add("inline-flex")
      }

      // Load currencies if per-company currency is enabled
      if (this.perCurrencyValue) {
        this._loadCurrencies(companyId)
      }
    } catch { /* ignore */ }
  }

  async _loadCurrencies(companyId) {
    if (!this.hasCurrencySectionTarget || !this.hasCurrencySelectTarget) return

    try {
      // Reuse the wizard's owner company currencies endpoint
      const wizardEl = this.element.closest("[data-controller*='project-wizard']")
      const currencies = wizardEl?.dataset.projectWizardCurrenciesValue
      if (!currencies) return

      const allCurrencies = JSON.parse(currencies)
      if (allCurrencies.length <= 1) return

      // Populate select — same format as Details step currency field
      let options = ""
      allCurrencies.forEach(c => {
        options += `<option value="${c.id}">${this._esc(c.symbol)} - ${this._esc(c.name || '')}</option>`
      })
      this.currencySelectTarget.innerHTML = options
      this.currencySectionTarget.classList.remove("hidden")
    } catch { /* ignore */ }
  }

  currencyChanged() {
    this._rebuildHiddenValue()
  }

  // ── Popover ─────────────────────────────────────────────────────────

  togglePopover() {
    if (!this.hasPopoverTarget) return

    if (this.popoverTarget.classList.contains("hidden")) {
      this._openPopover()
    } else {
      this._closePopover()
    }
  }

  // The Add Members pop-up (Phelipe 2026-10-08): a centered window with the
  // company's people on the left and who's on the project on the right.
  _openPopover() {
    if (this.hasPickerCompanyTarget) this.pickerCompanyTarget.textContent = this._companyName || ""
    if (this.hasPopoverSearchTarget) this.popoverSearchTarget.value = ""
    this._renderPopoverList("")
    this._renderSelectedList()
    if (this.hasPickerInviteRowTarget) {
      this.pickerInviteRowTarget.classList.add("hidden")
      this.pickerInviteInputTarget.value = ""
      this._showPickerInviteError("")
    }
    this.popoverTarget.classList.remove("hidden")
    this._onPickerKey = (e) => { if (e.key === "Escape") this._closePopover() }
    document.addEventListener("keydown", this._onPickerKey)
    if (this.hasPopoverSearchTarget) this.popoverSearchTarget.focus()
  }

  _closePopover() {
    this._whyTips?.hide()
    if (this.hasPopoverTarget) this.popoverTarget.classList.add("hidden")
    document.removeEventListener("keydown", this._onPickerKey)
  }

  closePicker() {
    this._closePopover()
  }

  // Only a click on the dimmed backdrop itself closes it.
  closePickerFromBackdrop(event) {
    if (event.target === event.currentTarget) this._closePopover()
  }

  // ── Invite New Member, inside the pop-up (Phelipe 2026-10-08) ─────
  // The email box opens right under the list; add one after another. Each
  // one lands on the right in blue with a "New" tag.
  openPickerInvite() {
    if (!this.hasPickerInviteRowTarget) return this.showInviteModal()
    this.pickerInviteRowTarget.classList.remove("hidden")
    this.pickerInviteInputTarget.focus()
  }

  pickerInviteKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.submitPickerInvite()
    } else if (event.key === "Escape") {
      event.stopPropagation() // closes the email box, not the whole pop-up
      this.pickerInviteRowTarget.classList.add("hidden")
    }
  }

  async submitPickerInvite() {
    const texts = pickerTexts(this.popoverTarget)
    const input = this.pickerInviteInputTarget
    const btn = this.pickerInviteBtnTarget
    const email = input.value.trim().toLowerCase()
    this._showPickerInviteError("")
    btn.disabled = true
    btn.textContent = texts.checking
    const error = await checkInviteEmail(email, { selected: this._selected, validateUrl: this.validateEmailUrlValue, texts })
    btn.disabled = false
    btn.textContent = texts.add
    if (error) return this._showPickerInviteError(error)

    this._invited ||= new Set()
    this._invited.add(email)
    this._selected.add(email)
    this._addChip(email, email)
    this._rebuildHiddenValue()
    this._renderSelectedList()
    input.value = ""
    input.focus()
  }

  _showPickerInviteError(message) {
    if (!this.hasPickerInviteErrorTarget) return
    this.pickerInviteErrorTarget.textContent = message
    this.pickerInviteErrorTarget.classList.toggle("hidden", !message)
  }

  _pickerOpen() {
    return this.hasPopoverTarget && !this.popoverTarget.classList.contains("hidden")
  }

  filterMembers() {
    const query = this.popoverSearchTarget.value.trim().toLowerCase()
    this._renderPopoverList(query)
  }

  _renderPopoverList(query) {
    if (!this.hasPopoverListTarget) return
    this.popoverListTarget.innerHTML = peopleRows({
      members: this._members, selected: this._selected, query, loaded: this._membersLoaded,
      ctrl: "team-card", texts: pickerTexts(this.popoverTarget)
    })
  }

  _renderSelectedList() {
    if (!this.hasSelectedListTarget) return
    const entries = Array.from(this._selected).map((email) => ({ email, label: this._names.get(email) || email, isNew: this._invited?.has(email) }))
    this.selectedListTarget.innerHTML = selectedRows({ entries, ctrl: "team-card", texts: pickerTexts(this.popoverTarget), color: this._chipColors().bg })
    if (this.hasSelectedCountTarget) this.selectedCountTarget.textContent = entries.length ? String(entries.length) : ""
  }

  // ── Member toggle ───────────────────────────────────────────────────

  toggleMember(event) {
    const checkbox = event.currentTarget
    const email = checkbox.value
    const name = checkbox.dataset.name

    if (checkbox.checked) {
      this._selected.add(email)
      this._addChip(name, email)
    } else {
      this._selected.delete(email)
      this._removeChipByEmail(email)
    }

    this._rebuildHiddenValue()
    this._renderSelectedList()
  }

  // ── Invite Modal ────────────────────────────────────────────────────

  showInviteModal() {
    this._closePopover()
    if (!this.hasInviteModalTarget) return

    // Set company name in header
    if (this.hasInviteModalCompanyTarget) {
      this.inviteModalCompanyTarget.textContent = this._companyName || ""
    }

    // Reset state
    if (this.hasInviteEmailInputTarget) this.inviteEmailInputTarget.value = ""
    if (this.hasInviteEmailErrorTarget) {
      this.inviteEmailErrorTarget.classList.add("hidden")
      this.inviteEmailErrorTarget.textContent = ""
    }
    if (this.hasInviteSubmitBtnTarget) {
      this.inviteSubmitBtnTarget.disabled = false
      this.inviteSubmitBtnTarget.textContent = "Add"
    }

    this.inviteModalTarget.classList.remove("hidden")
    setTimeout(() => this.inviteEmailInputTarget?.focus(), 50)
  }

  closeInviteModal() {
    if (this.hasInviteModalTarget) {
      this.inviteModalTarget.classList.add("hidden")
    }
  }

  inviteEmailKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.submitInviteEmail()
    } else if (event.key === "Escape") {
      this.closeInviteModal()
    }
  }

  async submitInviteEmail() {
    if (!this.hasInviteEmailInputTarget) return

    const email = this.inviteEmailInputTarget.value.trim().toLowerCase()

    // Client-side format check
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this._showInviteError("Please enter a valid email address.")
      return
    }

    // Check for duplicates
    if (this._selected.has(email)) {
      this._showInviteError("This member has already been added.")
      return
    }

    // Backend validation (MX check)
    if (this.validateEmailUrlValue) {
      this._setInviteLoading(true)
      try {
        const url = `${this.validateEmailUrlValue}?email=${encodeURIComponent(email)}`
        const resp = await fetch(url, { headers: { "Accept": "application/json" } })
        if (resp.ok) {
          const data = await resp.json()
          if (!data.is_valid) {
            this._setInviteLoading(false)
            this._showInviteError("This email address doesn't exist. Please double-check for typos and try again.")
            return
          }
        }
      } catch { /* proceed if validation endpoint fails */ }
      this._setInviteLoading(false)
    }

    // Valid — add chip and close
    this._invited ||= new Set()
    this._invited.add(email)
    this._selected.add(email)
    this._addChip(email, email)
    this._rebuildHiddenValue()
    this.closeInviteModal()
  }

  _showInviteError(msg) {
    if (!this.hasInviteEmailErrorTarget) return
    this.inviteEmailErrorTarget.textContent = msg
    this.inviteEmailErrorTarget.classList.remove("hidden")
    this.inviteEmailInputTarget?.classList.add("border-red-400", "ring-1", "ring-red-400")
  }

  _setInviteLoading(loading) {
    if (!this.hasInviteSubmitBtnTarget) return
    this.inviteSubmitBtnTarget.disabled = loading
    this.inviteSubmitBtnTarget.textContent = loading ? "Validating..." : "Add"
  }

  // ── Chips ─────────────────────────────────────────────────────────────

  _chipColors() {
    switch (this.roleValue) {
      case "contractors":
      case "bidding_contractors": return { bg: "#4A90E2", border: "#4A90E2" } // blue
      case "consultants":       return { bg: "#9B59B6", border: "#9B59B6" } // purple
      case "outsource_partners":
      case "re_consultants":    return { bg: "#6B7280", border: "#6B7280" } // gray
      case "support":
      case "stakeholders":      return { bg: "#6B7280", border: "#6B7280" } // gray
      default:                  return { bg: "#E67E22", border: "#E67E22" } // orange
    }
  }

  _addChip(label, email) {
    this._names.set(email, label || email)
    if (!this.hasMemberChipsTarget) return
    if (this.element.querySelector(`[data-chip-email="${CSS.escape(email)}"]`)) return

    // Someone invited by email is pink with a "New" tag (Phelipe 2026-10-08).
    const isNew = this._invited?.has(email)
    const bg = isNew ? newMemberColor(this._chipColors().bg) : this._chipColors().bg
    const chip = document.createElement("span")
    chip.dataset.teamCardTarget = "chip"
    chip.dataset.chipEmail = email
    chip.className = "inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium"
    chip.style.cssText = `font-size:14px;color:${bg};background:${bg}12;border:1px solid ${bg}4D;`
    chip.innerHTML = `
      ${this._esc(label)}${isNew ? newChipTag(this.hasPopoverTarget ? pickerTexts(this.popoverTarget) : null, bg) : ""}
      <button type="button" data-action="team-card#removeChip" data-email="${this._esc(email)}"
              class="ml-0.5 hover:text-red-500 cursor-pointer">&times;</button>
    `
    this.memberChipsTarget.appendChild(chip)
  }

  removeChip(event) {
    const email = event.currentTarget.dataset.email
    this._selected.delete(email)
    this._removeChipByEmail(email)
    this._rebuildHiddenValue()

    // Update checkbox in popover if open
    if (this.hasPopoverTarget && !this.popoverTarget.classList.contains("hidden")) {
      const cb = this.popoverTarget.querySelector(`input[type="checkbox"][value="${CSS.escape(email)}"]`)
      if (cb) cb.checked = false
      this._renderSelectedList()
    }
  }

  // ── Hidden Input ────────────────────────────────────────────────────

  _rebuildHiddenValue() {
    if (!this.hasHiddenEntryTarget || !this.hasCompanyHiddenTarget) return
    const companyId = this.companyHiddenTarget.value
    if (!companyId) return
    const chips = this.element.querySelectorAll("[data-chip-email]")
    const currencyId = this.hasCurrencySelectTarget ? this.currencySelectTarget.value : ""
    const pairs = Array.from(chips).map(c => {
      let entry = `${companyId}/${c.dataset.chipEmail}`
      if (currencyId) entry += `/${currencyId}`
      return entry
    })
    this.hiddenEntryTarget.value = pairs.join(" ")
  }

  // ── Remove Card ───────────────────────────────────────────────────────

  // Builder "Other Companies" cards (Kloepfer items 5-6): show the free-text
  // label input only when "Custom..." is picked in the company-type dropdown.
  flexRoleChanged() {
    if (!this.hasFlexRoleSelectTarget || !this.hasFlexRoleCustomTarget) return
    const isCustom = this.flexRoleSelectTarget.value === "__custom__"
    this.flexRoleCustomTarget.classList.toggle("hidden", !isCustom)
    if (isCustom) this.flexRoleCustomTarget.focus()
  }

  removeCard() {
    const chipCount = this.element.querySelectorAll("[data-team-card-target='memberChips'] [data-chip-email]").length

    // Blank/empty card — remove silently (common in the wizard's "Add Company" flow).
    if (chipCount === 0 || !this.hasRemoveModalTarget) {
      this.element.remove()
      return
    }

    // Card has members — confirm before removing to avoid an accidental wipe.
    const companyName = this._companyName || this.companyInputTarget?.value || "this company"
    if (this.hasRemoveModalCompanyTarget) this.removeModalCompanyTarget.textContent = companyName
    if (this.hasRemoveModalCountTarget)   this.removeModalCountTarget.textContent   = String(chipCount)
    this.removeModalTarget.classList.remove("hidden")
  }

  confirmRemove() {
    // Signal explicit user intent so the parent controller can tell the backend
    // this is a confirmed removal, not an accidental empty-payload submission.
    this.dispatch("explicit-remove", { detail: { role: this.roleValue }, bubbles: true })
    this.element.remove()
  }

  cancelRemove() {
    if (this.hasRemoveModalTarget) this.removeModalTarget.classList.add("hidden")
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  // ── Utilities ─────────────────────────────────────────────────────────

  _removeChipByEmail(email) {
    this.element.querySelectorAll(`span[data-chip-email="${CSS.escape(email)}"]`).forEach(el => el.remove())
  }

  // Safe in BOTH text and attribute position — quotes must be escaped too,
  // or a value carrying an inch mark (`6" VERTICAL CURB`) closes its own
  // attribute and swallows the rest of the tag, data-* attributes included.
  _esc(str) {
    const d = document.createElement("div")
    d.textContent = str || ""
    return d.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
